import React, { useRef, useState } from "react";
import { Form, Row, Col, Button, Badge } from "react-bootstrap";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ConditionalLogicEditor from "./ConditionalLogicEditor";
import DynamicFormRenderer from "./DynamicFormRenderer";
import ConfirmModal from "../common/ConfirmModal";
import EmployeePicker from "../shared/EmployeePicker";
import { SCORING_MODES } from "../../pages/forms/formBuilderNormalizers";

const RANGE_CAPABLE_TYPES = new Set(["NUMBER", "DECIMAL", "RATING", "SCORE", "PERCENTAGE"]);
const LENGTH_CAPABLE_TYPES = new Set(["SHORT_TEXT", "LONG_TEXT", "EMAIL", "MOBILE", "URL"]);
const OPTION_CAPABLE_TYPES = new Set(["DROPDOWN", "MULTI_SELECT", "RADIO", "CHECKBOX", "YES_NO", "RATING", "RESULT_STATUS"]);
const MULTI_VALUE_TYPES = new Set(["MULTI_SELECT", "CHECKBOX"]);
const SCORING_MODE_LABELS = { NONE: "None", AUTO: "Automatic", MANUAL: "Manual", AUTO_WITH_REVIEW: "Automatic + Review" };
const SCORING_AGGREGATIONS = ["CAPPED_SUM", "SUM", "MAX", "AVERAGE"];

// Phase 3D — see PHASE_3D_DATABASE_DESIGN.md §1. Optional on every field;
// used by the Execution Profile tab to resolve UAT/Testing fields by role,
// never by label.
const SEMANTIC_ROLES = [
  "TEST_CASE_ID", "TEST_CASE_TITLE", "TEST_CASE_DESCRIPTION", "PRECONDITION", "TEST_STEPS",
  "EXPECTED_RESULT", "ACTUAL_RESULT", "RESULT_STATUS", "SEVERITY", "DEFECT_SUMMARY",
  "DEFECT_DESCRIPTION", "DEFECT_PRIORITY", "TEST_DATA", "ENVIRONMENT", "BUILD_VERSION",
  "RELEASE_VERSION", "EVIDENCE", "REMARKS",
];

let localIdCounter = 0;
function nextLocalId() {
  localIdCounter -= 1;
  return localIdCounter;
}

// Remove Option, §7: conditions store a raw option_code string (see
// ConditionalLogicEditor.jsx's free-text value control), matched at
// evaluation time against whatever the field actually submits --
// DynamicFormRenderer.jsx uses option.option_code as the option's value
// for every option-capable type. There is no foreign key from a condition
// to a specific option row, so "is this option referenced elsewhere" can
// only be answered by scanning every OTHER field's own
// conditional_visibility_json for a condition on THIS field whose value
// matches the option being removed -- exactly mirroring
// services/formConditionalLogicService.js's evaluateCondition() shape
// (EQUALS/NOT_EQUALS compare a single value, IN/NOT_IN compare an array).
export function findConditionalDependents(allFields, ownFieldKey, optionCode) {
  if (!optionCode) return [];
  const code = String(optionCode);
  const dependents = [];
  for (const other of allFields || []) {
    if (!other || other.field_key === ownFieldKey) continue;
    let config = other.conditional_visibility_json;
    if (!config) continue;
    if (typeof config === "string") {
      try { config = JSON.parse(config); } catch (_e) { continue; }
    }
    const conditions = Array.isArray(config.conditions) ? config.conditions : [];
    const references = conditions.some((c) => {
      if (c.field_key !== ownFieldKey) return false;
      if (Array.isArray(c.value)) return c.value.map(String).includes(code);
      return String(c.value ?? "") === code;
    });
    if (references) dependents.push(other.label || other.field_key);
  }
  return dependents;
}

// Phase 3A. Edits one form_fields row (plus its form_field_options, inline)
// — label/key/type, required/read-only, validation ranges, scoring
// metadata, table/export/print flags, and conditional visibility. Options
// only shown for OPTION_CAPABLE_TYPES (§9's field-type matrix).
export default function FormFieldEditor({ field, otherFieldKeys, allFields, onChange, readOnly, errors }) {
  // errors -- see formValidationMapping.js's buildFieldErrorMap() -- is a
  // list of { target, message } for THIS field. Only "scoring-mode" is
  // rendered as a control-level invalid state today (the one confirmed,
  // reproducible case); "field-type"/"weightage" targets still show via
  // the field card's own inline banner (FormCanvas.jsx) and the top issue
  // panel, so nothing is silently dropped -- this scopes the red-border-
  // on-one-control treatment to the control that actually has a UI
  // representation of "value" to mark invalid.
  const scoringModeError = (errors || []).find((e) => e.target === "scoring-mode");
  const dragOptionRef = useRef(null);
  const [optionDragOver, setOptionDragOver] = useState(null);
  // Remove Option flow state: null while no confirm dialog is open;
  // otherwise { index, option, dependents } for whichever row's trash
  // icon was just clicked. `dependents` (field labels/keys) is computed
  // up front so the confirm dialog can decide, without re-scanning later,
  // whether to show the normal "Remove option?" prompt or the blocking
  // "used by conditional logic" notice (§7).
  const [pendingRemoveOption, setPendingRemoveOption] = useState(null);

  const update = (patch) => onChange({ ...field, ...patch });

  const updateOption = (index, patch) => {
    const options = [...(field.options || [])];
    options[index] = { ...options[index], ...patch };
    update({ options });
  };

  const addOption = () => {
    update({
      options: [
        ...(field.options || []),
        { id: nextLocalId(), option_code: "", option_label: "", option_value: "", score_value: "", is_not_applicable: false, is_default: false, display_order: (field.options || []).length },
      ],
    });
  };

  // Opens the confirmation dialog rather than deleting immediately (§2).
  // Whether the option is persisted (positive id) or was only just added
  // locally (negative local id from nextLocalId()) makes no difference
  // here -- either way this only ever touches local state; removeOption
  // below never calls an API itself. Persistence for BOTH cases goes
  // through the exact same existing save path: FormBuilderPage.jsx's
  // saveAll() sends the field's current `options` array (this option
  // simply absent) to the existing PUT .../options endpoint, whose
  // services/formFieldService.js#replaceOptions already deletes any
  // option row not present in that payload (or never inserts an
  // unsaved one) -- the existing upsert-by-diff mechanism IS the delete,
  // reused as-is, never a new per-option DELETE endpoint (§3/§4/§5).
  const requestRemoveOption = (index) => {
    const option = (field.options || [])[index];
    const dependents = findConditionalDependents(allFields, field.field_key, option?.option_code);
    setPendingRemoveOption({ index, option, dependents });
  };

  const cancelRemoveOption = () => setPendingRemoveOption(null);

  const confirmRemoveOption = () => {
    if (!pendingRemoveOption || pendingRemoveOption.dependents.length) return;
    const { index, option } = pendingRemoveOption;
    const options = (field.options || []).filter((_, i) => i !== index);
    const patch = { options };
    // §6: form_field_options.is_default is a flag ON the option row
    // itself, so deleting the row already clears it -- nothing dangles.
    // form_fields.default_value, however, is a SEPARATE freeform column
    // (audited: never populated from is_default, never read back against
    // options anywhere in DynamicFormRenderer.jsx or the backend) that an
    // admin could still have typed an option's code/value into by hand --
    // defensively clear it only if it literally equals the option just
    // removed, rather than ever auto-picking a new default (no existing
    // business rule does that).
    if (option && field.default_value != null && String(field.default_value) !== "") {
      const removedValues = [option.option_code, option.option_value].filter((v) => v != null).map(String);
      if (removedValues.includes(String(field.default_value))) {
        patch.default_value = "";
      }
    }
    update(patch);
    setPendingRemoveOption(null);
  };

  // Options must persist display_order through the existing option save
  // API (see FormBuilderPage.jsx's saveAll -> PUT .../options) -- reordering
  // here just changes local array position; saveAll() re-numbers
  // display_order from that position when it saves, same as it already
  // does for sections/fields.
  const reorderOption = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const options = [...(field.options || [])];
    const [moved] = options.splice(fromIndex, 1);
    options.splice(toIndex, 0, moved);
    update({ options });
  };

  const showRange = RANGE_CAPABLE_TYPES.has(field.field_type);
  const showLength = LENGTH_CAPABLE_TYPES.has(field.field_type);
  const showOptions = OPTION_CAPABLE_TYPES.has(field.field_type);
  const isStatic = field.field_type === "SECTION_HEADING" || field.field_type === "STATIC_TEXT";

  // Live mini-preview -- reuses the exact same shared renderer the full
  // Preview step uses (see FormPreview.jsx), on a synthetic one-field
  // section. conditional_visibility_json is stripped for this preview only
  // (a local copy, never sent to update()/onChange) so a field whose
  // visibility depends on ANOTHER field's value doesn't render blank here
  // just because that other field has no value in this isolated preview.
  const previewSections = [{ id: "mini-preview", title: "", fields: [{ ...field, conditional_visibility_json: null }] }];

  return (
    <div className="form-field-editor">
      <div className="form-properties-panel-heading d-flex justify-content-between align-items-center">
        <span>Field Properties</span>
        <Badge bg="secondary">{field.field_type}</Badge>
      </div>

      <div className="form-props-group">
        <div className="form-props-group-label">Basic</div>
        <Row className="g-2">
          <Col md={6}>
            <Form.Label className="small">Label</Form.Label>
            <Form.Control size="sm" disabled={readOnly} value={field.label || ""} onChange={(e) => update({ label: e.target.value })} />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Field Key (API-safe, unique in version)</Form.Label>
            <Form.Control size="sm" disabled={readOnly} value={field.field_key || ""} onChange={(e) => update({ field_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} />
          </Col>
          <Col md={6}>
            <Form.Label className="small">Semantic Role (optional — for UAT/Testing execution profiles)</Form.Label>
            <Form.Select size="sm" disabled={readOnly} value={field.semantic_role || ""} onChange={(e) => update({ semantic_role: e.target.value || null })}>
              <option value="">None</option>
              {SEMANTIC_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small">Description (optional)</Form.Label>
            <Form.Control size="sm" disabled={readOnly} value={field.description || ""} onChange={(e) => update({ description: e.target.value })} />
          </Col>

          {!isStatic && (
            <>
              <Col md={6}>
                <Form.Label className="small">Placeholder</Form.Label>
                <Form.Control size="sm" disabled={readOnly} value={field.placeholder || ""} onChange={(e) => update({ placeholder: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Help Text</Form.Label>
                <Form.Control size="sm" disabled={readOnly} value={field.help_text || ""} onChange={(e) => update({ help_text: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label className="small">Default Value</Form.Label>
                <Form.Control size="sm" disabled={readOnly} value={field.default_value || ""} onChange={(e) => update({ default_value: e.target.value })} />
              </Col>
            </>
          )}
        </Row>
      </div>

      {!isStatic && (
        <div className="form-props-group">
          <div className="form-props-group-label">Behaviour</div>
          <Row className="g-2">
            <Col md={4}>
              <Form.Check type="checkbox" label="Required" disabled={readOnly} checked={!!field.is_required} onChange={(e) => update({ is_required: e.target.checked })} />
            </Col>
            <Col md={4}>
              <Form.Check type="checkbox" label="Read-only" disabled={readOnly} checked={!!field.is_read_only} onChange={(e) => update({ is_read_only: e.target.checked })} />
            </Col>

            {showRange && (
              <>
                <Col md={3}>
                  <Form.Label className="small">Min Value</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.minimum_value ?? ""} onChange={(e) => update({ minimum_value: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Max Value</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.maximum_value ?? ""} onChange={(e) => update({ maximum_value: e.target.value })} />
                </Col>
              </>
            )}
            {showLength && (
              <>
                <Col md={3}>
                  <Form.Label className="small">Min Length</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.minimum_length ?? ""} onChange={(e) => update({ minimum_length: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Max Length</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.maximum_length ?? ""} onChange={(e) => update({ maximum_length: e.target.value })} />
                </Col>
              </>
            )}

            <Col md={4}>
              <Form.Check type="switch" label="Include in response table" disabled={readOnly} checked={field.include_in_response_table !== false} onChange={(e) => update({ include_in_response_table: e.target.checked })} />
            </Col>
            <Col md={4}>
              <Form.Check type="switch" label="Include in export" disabled={readOnly} checked={field.include_in_export !== false} onChange={(e) => update({ include_in_export: e.target.checked })} />
            </Col>
            <Col md={4}>
              <Form.Check type="switch" label="Include in print" disabled={readOnly} checked={field.include_in_print !== false} onChange={(e) => update({ include_in_print: e.target.checked })} />
            </Col>
          </Row>
        </div>
      )}

      {!isStatic && (
        <div className="form-props-group">
          <div className="form-props-group-label">Scoring</div>
          <Row className="g-2">
            <Col md={12}>
              <Form.Check
                type="checkbox"
                label="Score enabled"
                disabled={readOnly}
                checked={!!field.score_enabled}
                onChange={(e) => {
                  const scoreEnabled = e.target.checked;
                  if (!scoreEnabled) {
                    // Unscored fields can only ever mean NONE -- clearing
                    // this immediately (rather than leaving a stale mode
                    // hidden behind the collapsed scoring controls below)
                    // is what keeps a field from failing publish with
                    // "scoring_mode must be one of..." after being
                    // unchecked and saved without anyone touching the
                    // (now-hidden) Scoring Mode dropdown.
                    update({ score_enabled: false, scoring_mode: "NONE", allow_manual_score: false, manual_score_required: false });
                    return;
                  }
                  // Re-enabling scoring: default to AUTO only if there is
                  // no valid mode already set -- never overwrite an
                  // existing deliberate MANUAL/AUTO_WITH_REVIEW choice.
                  const patch = { score_enabled: true };
                  if (!SCORING_MODES.includes(field.scoring_mode) || field.scoring_mode === "NONE") {
                    patch.scoring_mode = "AUTO";
                  }
                  update(patch);
                }}
              />
            </Col>

            {field.score_enabled && (
              <>
                <Col md={6}>
                  <Form.Label className="small">Max Score</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.maximum_score ?? ""} onChange={(e) => update({ maximum_score: e.target.value })} />
                </Col>
                <Col md={6}>
                  <Form.Label className="small">Weightage %</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={100} disabled={readOnly} value={field.weightage ?? ""} onChange={(e) => update({ weightage: e.target.value })} />
                </Col>
                <Col md={12}>
                  <Form.Label className="small">
                    Scoring Mode {scoringModeError && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Select
                    disabled={readOnly}
                    value={SCORING_MODES.includes(field.scoring_mode) ? field.scoring_mode : "NONE"}
                    isInvalid={!!scoringModeError}
                    aria-invalid={!!scoringModeError}
                    aria-describedby={scoringModeError ? "scoring-mode-error" : undefined}
                    onChange={(e) => {
                      const mode = e.target.value;
                      update({
                        scoring_mode: mode,
                        allow_manual_score: mode === "MANUAL" || mode === "AUTO_WITH_REVIEW",
                        manual_score_required: mode === "MANUAL",
                      });
                    }}
                  >
                    {SCORING_MODES.map((m) => <option key={m} value={m}>{SCORING_MODE_LABELS[m]}</option>)}
                  </Form.Select>
                  {scoringModeError && (
                    <div id="scoring-mode-error" className="invalid-feedback d-block">
                      ⚠ Select a valid scoring mode.
                    </div>
                  )}
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Score Decimal Places</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={4} disabled={readOnly} value={field.score_precision ?? 2} onChange={(e) => update({ score_precision: e.target.value })} />
                </Col>

                {MULTI_VALUE_TYPES.has(field.field_type) && (
                  <Col md={3}>
                    <Form.Label className="small">Multi-select Aggregation</Form.Label>
                    <Form.Select size="sm" disabled={readOnly} value={field.scoring_aggregation || "CAPPED_SUM"} onChange={(e) => update({ scoring_aggregation: e.target.value })}>
                      {SCORING_AGGREGATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </Form.Select>
                  </Col>
                )}
                {OPTION_CAPABLE_TYPES.has(field.field_type) && (
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Check
                      type="checkbox"
                      label="Exclude N/A from score"
                      disabled={readOnly}
                      checked={field.exclude_not_applicable_from_score !== false}
                      onChange={(e) => update({ exclude_not_applicable_from_score: e.target.checked })}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <Form.Label className="small">Min Passing Score</Form.Label>
                  <Form.Control size="sm" type="number" disabled={readOnly} value={field.minimum_passing_score ?? ""} onChange={(e) => update({ minimum_passing_score: e.target.value })} />
                </Col>
                {(field.scoring_mode === "MANUAL" || field.scoring_mode === "AUTO_WITH_REVIEW") && (
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Check
                      type="checkbox"
                      label="Manual score required to finalize"
                      disabled={readOnly}
                      checked={!!field.manual_score_required}
                      onChange={(e) => update({ manual_score_required: e.target.checked })}
                    />
                  </Col>
                )}
              </>
            )}
          </Row>
        </div>
      )}

      {showOptions && (
        <div className="form-props-group form-field-options-editor">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="form-props-group-label mb-0">Options{field.field_type === "RATING" ? " (1 = lowest ... N = highest)" : ""}</div>
            {!readOnly && <Button size="sm" variant="outline-primary" onClick={addOption}>+ Add Option</Button>}
          </div>
          {(field.options || []).map((option, index) => (
            <div
              key={option.id}
              className={`form-field-option-row${optionDragOver === index ? " is-drag-over" : ""}`}
              draggable={!readOnly}
              onDragStart={readOnly ? undefined : (e) => { dragOptionRef.current = index; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(index)); }}
              onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (optionDragOver !== index) setOptionDragOver(index); }}
              onDrop={readOnly ? undefined : (e) => {
                e.preventDefault();
                const from = dragOptionRef.current;
                setOptionDragOver(null);
                dragOptionRef.current = null;
                if (from == null || from === index) return;
                reorderOption(from, index);
              }}
              onDragEnd={() => { dragOptionRef.current = null; setOptionDragOver(null); }}
            >
              {!readOnly && (
                <span className="form-field-option-handle" title="Drag to reorder" aria-hidden="true">
                  <DragIndicatorIcon fontSize="small" />
                </span>
              )}
              {field.is_subject_field ? (
                // Task spec §14: this field identifies WHO a submission is
                // about (form_fields.is_subject_field, set from the KRA/KPI
                // Evidence Mapping tab) -- each of its options must carry a
                // real users.id, picked by search, never typed as a raw
                // option_code. option_code is still generated (stable,
                // derived from the account) so existing option_code-based
                // conditional-logic/legacy code paths keep working
                // unchanged; it is never hand-typed here.
                <div className="form-field-option-employee-picker" style={{ minWidth: 260, flex: 1 }}>
                  <EmployeePicker
                    endpoint="/forms/employee-options/search"
                    isDisabled={readOnly}
                    value={option.user_id ?? null}
                    onChange={(userId, user) => updateOption(index, {
                      user_id: userId,
                      option_label: user ? (user.name || option.option_label) : option.option_label,
                      option_code: userId ? `EMP_${userId}` : option.option_code,
                    })}
                  />
                </div>
              ) : (
                <>
                  <Form.Control size="sm" placeholder="Code" disabled={readOnly} value={option.option_code || ""} onChange={(e) => updateOption(index, { option_code: e.target.value.toUpperCase() })} className="form-field-option-code" />
                  <Form.Control size="sm" placeholder="Label" disabled={readOnly} value={option.option_label || ""} onChange={(e) => updateOption(index, { option_label: e.target.value })} className="form-field-option-label" />
                </>
              )}
              <Form.Control size="sm" type="number" placeholder="Score" disabled={readOnly} value={option.score_value ?? ""} onChange={(e) => updateOption(index, { score_value: e.target.value })} className="form-field-option-score" />
              <Form.Check type="checkbox" title="Default selection" label="Def." disabled={readOnly} checked={!!option.is_default} onChange={(e) => updateOption(index, { is_default: e.target.checked })} />
              <Form.Check type="checkbox" title="Not applicable option" label="N/A" disabled={readOnly} checked={!!option.is_not_applicable} onChange={(e) => updateOption(index, { is_not_applicable: e.target.checked })} />
              {!readOnly && (
                <button type="button" className="form-canvas-icon-btn form-canvas-icon-btn-danger" title="Remove option" aria-label="Remove option" onClick={() => requestRemoveOption(index)}>
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              )}
            </div>
          ))}
          {!(field.options || []).length && <div className="text-muted small">No options yet — at least one is required before publishing.</div>}
        </div>
      )}

      {pendingRemoveOption && !pendingRemoveOption.dependents.length && (
        <ConfirmModal
          show
          onHide={cancelRemoveOption}
          onConfirm={confirmRemoveOption}
          title="Remove option?"
          message={`Remove "${pendingRemoveOption.option?.option_label || pendingRemoveOption.option?.option_code || "this option"}" from ${field.label || field.field_key}?`}
          confirmLabel="Remove Option"
        />
      )}

      {pendingRemoveOption && !!pendingRemoveOption.dependents.length && (
        <ConfirmModal
          show
          onHide={cancelRemoveOption}
          onConfirm={cancelRemoveOption}
          title="Cannot remove option"
          message={`This option is used by conditional logic in ${pendingRemoveOption.dependents.length} field${pendingRemoveOption.dependents.length === 1 ? "" : "s"} (${pendingRemoveOption.dependents.join(", ")}). Remove those conditions before deleting this option.`}
          confirmLabel="OK"
          confirmVariantClass="unit-btn-light"
        />
      )}

      {!isStatic && (
        <div className="form-props-group">
          <div className="form-props-group-label">Conditional Logic</div>
          <ConditionalLogicEditor
            value={field.conditional_visibility_json}
            otherFieldKeys={otherFieldKeys}
            onChange={(config) => update({ conditional_visibility_json: config })}
            readOnly={readOnly}
          />
        </div>
      )}

      {!isStatic && (
        <div className="form-field-mini-preview">
          <div className="form-props-group-label">Live Preview</div>
          <div className="form-field-mini-preview-frame">
            <DynamicFormRenderer sections={previewSections} mode="preview" />
          </div>
        </div>
      )}
    </div>
  );
}
